import { useState } from "react";
import { useNavigate } from "react-router-dom";
import { TonConnectButton, useTonWallet } from "@tonconnect/ui-react";
import { useAppData, type ActiveChallenge } from "../context/AppDataContext";
import Avatar from "../components/Avatar";
import PullToRefresh from "../components/PullToRefresh";
import type { Challenge } from "../lib/api";
import { shareChallenge } from "../lib/share";
import { api } from "../lib/api";

function getStatusColor(status: Challenge["status"]): string {
  switch (status) {
    case "OPEN":
      return "green";
    case "LOCKED":
      return "yellow";
    case "RESOLVING":
      return "orange";
    default:
      return "gray";
  }
}

function getStatusLabel(status: Challenge["status"]): string {
  switch (status) {
    case "OPEN":
      return "Active";
    case "LOCKED":
      return "Locked";
    case "RESOLVING":
      return "Voting";
    default:
      return status;
  }
}

function formatTimeRemaining(time: string | null): { text: string; isExpired: boolean } | null {
  if (!time) return null;

  const target = new Date(time);
  const now = new Date();
  const diff = target.getTime() - now.getTime();

  if (diff <= 0) return { text: "Expired", isExpired: true };

  const hours = Math.floor(diff / (1000 * 60 * 60));
  const minutes = Math.floor((diff % (1000 * 60 * 60)) / (1000 * 60));

  if (hours > 24) {
    const days = Math.floor(hours / 24);
    return { text: `${days}d ${hours % 24}h left`, isExpired: false };
  }

  if (hours > 0) {
    return { text: `${hours}h ${minutes}m left`, isExpired: false };
  }

  return { text: `${minutes}m left`, isExpired: false };
}

function HomeTab() {
  const navigate = useNavigate();
  const wallet = useTonWallet();
  const { user, demoMode, activeChallenges, stats, isLoading, error, refresh, updateBalance } = useAppData();
  const [cancellingId, setCancellingId] = useState<string | null>(null);

  // Get current user
  const currentUser = window.Telegram?.WebApp?.initDataUnsafe?.user;

  const handleShare = (challenge: ActiveChallenge, e: React.MouseEvent) => {
    e.stopPropagation();
    shareChallenge(challenge.id);
  };

  const handleCancel = (challengeId: string, e: React.MouseEvent) => {
    e.stopPropagation();

    const doCancel = async () => {
      setCancellingId(challengeId);
      try {
        const result = await api.cancelChallenge(challengeId);
        if (result.userBalance !== null) {
          updateBalance(result.userBalance);
        }
        await refresh();
      } catch (err) {
        console.error("Failed to cancel:", err);
      } finally {
        setCancellingId(null);
      }
    };

    if (window.Telegram?.WebApp) {
      window.Telegram.WebApp.showConfirm(
        "Cancel this challenge? Your contribution will be refunded.",
        (confirmed) => {
          if (confirmed) doCancel();
        }
      );
    } else if (confirm("Cancel this challenge? Your contribution will be refunded.")) {
      doCancel();
    }
  };

  // Check if current user is the creator of a challenge
  const isCreator = (challenge: ActiveChallenge) => {
    return challenge.creator?.telegramId === currentUser?.id?.toString();
  };

  const hasStats = stats.wins > 0 || stats.losses > 0;

  return (
    <PullToRefresh onRefresh={refresh}>
      <div className="tab-content home">
        <div className="header">
        <h1>Pollygram</h1>
        <p className="tagline">Settle arguments with friends</p>
      </div>

      {/* Stats Bar */}
      {hasStats && (
        <div className="stats-bar">
          <div className="stat">
            <span className="stat-value wins">{stats.wins}W</span>
            <span className="stat-label">Wins</span>
          </div>
          <div className="stat-divider" />
          <div className="stat">
            <span className="stat-value losses">{stats.losses}L</span>
            <span className="stat-label">Losses</span>
          </div>
          <div className="stat-divider" />
          <div className="stat">
            <span className={`stat-value ${stats.totalEarned >= 0 ? "earned" : "lost"}`}>
              {stats.totalEarned >= 0 ? "+" : ""}{stats.totalEarned.toFixed(1)}
            </span>
            <span className="stat-label">TON net</span>
          </div>
        </div>
      )}

      {/* Balance Card (Demo Mode) */}
      {demoMode && user && (
        <div className="balance-card">
          <span className="label">Demo Balance</span>
          <span className="amount">{user.balance.toFixed(2)} TON</span>
        </div>
      )}

      {/* Wallet Section (Real Mode) */}
      {!demoMode && (
        <div className="wallet-section">
          <TonConnectButton />
        </div>
      )}

      {isLoading ? (
        <div className="loading-state">Loading...</div>
      ) : error ? (
        <div className="error-state">
          <p>{error}</p>
          <p className="debug-info">
            Platform: {window.Telegram?.WebApp?.platform || "unknown"}<br/>
            initData: {window.Telegram?.WebApp?.initData ? "present" : "empty"}<br/>
            User ID: {window.Telegram?.WebApp?.initDataUnsafe?.user?.id || "none"}
          </p>
        </div>
      ) : (
        <>
          {/* Active Challenges Section */}
          {(demoMode || wallet) && activeChallenges.length > 0 && (
            <div className="active-section">
              <div className="section-header">
                <h3>Active Challenges</h3>
                <span className="challenge-count">{activeChallenges.length}</span>
              </div>
              <div className="active-list">
                {activeChallenges.map((challenge) => {
                  const expirationInfo = formatTimeRemaining(challenge.closeTime);
                  const isExpired = expirationInfo?.isExpired;
                  const timerInfo = challenge.timerEndsAt ? formatTimeRemaining(challenge.timerEndsAt) : null;

                  // Check if challenge has a timer and if it has expired
                  const hasTimer = !!challenge.timerEndsAt;
                  const timerExpired = timerInfo?.isExpired || false;
                  const canResolve = !hasTimer || timerExpired;

                  return (
                    <div
                      key={challenge.id}
                      className={`active-item ${challenge.needsAction ? "needs-action" : ""}`}
                      onClick={() => navigate(`/challenge/${challenge.id}`)}
                    >
                      <div className="active-matchup">
                        <Avatar username={currentUser?.username || "You"} photoUrl={user?.telegramPhotoUrl} size="sm" />
                        <span className="matchup-you">You</span>
                        <span className="matchup-vs">vs</span>
                        {challenge.opponentUsername ? (
                          <>
                            <Avatar username={challenge.opponentUsername} photoUrl={challenge.opponentPhotoUrl} size="sm" />
                            <span className="opponent-name">@{challenge.opponentUsername}</span>
                          </>
                        ) : (
                          <>
                            <div className="avatar-placeholder-sm">?</div>
                            <span className="opponent-name waiting">?</span>
                          </>
                        )}
                      </div>
                      <p className="active-description">"{challenge.description}"</p>

                      <div className="active-info">
                        <span className={`status ${getStatusColor(challenge.status)}`}>
                          {getStatusLabel(challenge.status)}
                        </span>
                        <span className="pool">{challenge.poolTotal} TON</span>
                        {isCreator(challenge) ? (
                          <span className="creator-badge you">You</span>
                        ) : (
                          <span className="creator-badge">@{challenge.creator?.telegramUsername || "user"}</span>
                        )}
                      </div>

                      {/* Expiration timer for OPEN challenges */}
                      {challenge.status === "OPEN" && expirationInfo && (
                        <div className={`timer ${expirationInfo.isExpired ? "expired" : ""}`}>
                          {expirationInfo.text}
                        </div>
                      )}

                      {/* Countdown timer for LOCKED challenges */}
                      {challenge.status === "LOCKED" && timerInfo && (
                        <div className={`challenge-timer ${timerInfo.isExpired ? "timer-expired" : ""}`}>
                          <span className="timer-icon">⏱️</span>
                          {timerInfo.isExpired ? "Time's up!" : timerInfo.text}
                        </div>
                      )}

                      {/* Action badges */}
                      {challenge.needsAction && challenge.status === "RESOLVING" && (
                        <div className="action-badge-row">
                          <span className="action-badge">Your turn to vote!</span>
                        </div>
                      )}
                      {challenge.status === "RESOLVING" && challenge.userVote && (
                        <div className="waiting-badge-row">
                          <span className="their-turn">Waiting for opponent's vote...</span>
                        </div>
                      )}

                      {/* Action buttons */}
                      <div className="active-actions">
                        {challenge.status === "OPEN" && !isExpired && (
                          <>
                            <button
                              className="btn small secondary"
                              onClick={(e) => handleShare(challenge, e)}
                            >
                              Share
                            </button>
                            {isCreator(challenge) && (
                              <button
                                className="btn small text"
                                onClick={(e) => handleCancel(challenge.id, e)}
                                disabled={cancellingId === challenge.id}
                              >
                                {cancellingId === challenge.id ? "..." : "Cancel"}
                              </button>
                            )}
                          </>
                        )}
                        {challenge.status === "LOCKED" && canResolve && (
                          <button
                            className="btn small primary"
                            onClick={(e) => {
                              e.stopPropagation();
                              navigate(`/resolve/${challenge.id}`);
                            }}
                          >
                            Start Resolution
                          </button>
                        )}
                        {challenge.status === "RESOLVING" && !challenge.userVote && (
                          <button
                            className="btn small primary"
                            onClick={(e) => {
                              e.stopPropagation();
                              navigate(`/resolve/${challenge.id}`);
                            }}
                          >
                            Vote Now
                          </button>
                        )}
                      </div>
                    </div>
                  );
                })}
              </div>
            </div>
          )}

          {/* Empty State for Active Challenges */}
          {(demoMode || wallet) && activeChallenges.length === 0 && (
            <div className="empty-active">
              <p>No active challenges</p>
              <p className="empty-hint">Tap Create to challenge a friend!</p>
            </div>
          )}

          {/* Onboarding (not connected) */}
          {!demoMode && !wallet && (
            <div className="onboarding">
              <div className="step">
                <span className="step-number">1</span>
                <p>Connect your TON wallet</p>
              </div>
              <div className="step">
                <span className="step-number">2</span>
                <p>Create or join a challenge</p>
              </div>
              <div className="step">
                <span className="step-number">3</span>
                <p>Stake TON and prove who's right</p>
              </div>
            </div>
          )}
        </>
      )}
      </div>
    </PullToRefresh>
  );
}

export default HomeTab;
